import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { api, extractError } from "@/lib/api";
import { useI18n } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";

export function PaymentSuccess() {
  const { lang } = useI18n();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const provider = params.get("provider") || "stripe";
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const [error, setError] = useState("");
  const attemptRef = useRef(0);

  const poll = useCallback(async () => {
    if (!sessionId && provider !== "paypal") {
      setStatus("failed");
      setError("Missing session id");
      return;
    }
    attemptRef.current += 1;
    try {
      if (provider === "paypal") {
        // PayPal completion is asynchronous via webhook. Poll the user
        // record via /auth/me until membership_status becomes active.
        const { data } = await api.get("/auth/me");
        if (data?.membership_status === "active") {
          setStatus("paid");
          await refresh();
          return;
        }
      } else {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setStatus("failed");
          return;
        }
      }
    } catch (e) {
      setError(extractError(e));
    }
    if (attemptRef.current < 12) {
      setTimeout(poll, 2500);
    } else {
      setStatus("failed");
      setError("Timed out waiting for payment confirmation.");
    }
  }, [sessionId, provider, refresh]);

  useEffect(() => {
    poll();
  }, [poll]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
              NO VAX LOVE
            </p>
            <h1 className="mt-3 text-4xl font-display">
              {lang === "it" ? "Verifica pagamento…" : "Confirming payment…"}
            </h1>
            <p className="mt-3 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {lang === "it" ? "Un momento, controlliamo con Stripe." : "One moment while we confirm with Stripe."}
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={40} style={{ color: "hsl(var(--nvl-accent))" }} className="mx-auto" />
            <h1 className="mt-4 text-4xl font-display" data-testid="payment-success-title">
              {lang === "it" ? "Iscrizione attiva!" : "Membership active!"}
            </h1>
            <p className="mt-3 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {lang === "it"
                ? "Benvenuto nella community. Tutte le funzioni sono ora sbloccate."
                : "Welcome to the community. All features are now unlocked."}
            </p>
            <button
              onClick={() => navigate("/app/discover")}
              className="mt-8 nvl-btn nvl-btn-primary"
              data-testid="payment-success-continue"
            >
              {lang === "it" ? "Inizia a scoprire" : "Start discovering"}
            </button>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle size={40} style={{ color: "hsl(var(--destructive))" }} className="mx-auto" />
            <h1 className="mt-4 text-4xl font-display">
              {lang === "it" ? "Impossibile confermare" : "Unable to confirm"}
            </h1>
            <p className="mt-3 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }} data-testid="payment-error">
              {error || (lang === "it" ? "Riprova più tardi." : "Please try again later.")}
            </p>
            <Link to="/app/membership" className="mt-8 inline-flex nvl-btn nvl-btn-outline">
              {lang === "it" ? "Torna ai piani" : "Back to plans"}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const { lang } = useI18n();
  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="payment-cancel-page">
      <div className="max-w-md w-full text-center">
        <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
          NO VAX LOVE
        </p>
        <h1 className="mt-3 text-4xl font-display">
          {lang === "it" ? "Pagamento annullato" : "Payment canceled"}
        </h1>
        <p className="mt-3 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {lang === "it"
            ? "Nessun addebito. Puoi riprovare quando vuoi."
            : "No charge made. You can try again any time."}
        </p>
        <Link to="/app/membership" className="mt-8 inline-flex nvl-btn nvl-btn-primary">
          {lang === "it" ? "Torna ai piani" : "Back to plans"}
        </Link>
      </div>
    </div>
  );
}
